import React, { useEffect, useState } from "react";
import VsOpsLayout from "./VsOpsLayout";
import { VsOpsRequireAuth } from "./VsOpsLoginPage";
import { vsOpsApi } from "./api";
import { formatRubPrice, formatUsdtPrice } from "../visoria/helpers";

const emptyForm = {
  name: "",
  refCode: "",
  email: "",
  commissionPercent: "2",
  paymentWindowDays: "30",
  customerLifetimeDays: "",
  isActive: true,
};

function formatLifetimeDays(value) {
  if (value == null || value === "") {
    return "Навсегда";
  }
  return `${value} дн.`;
}

function VsOpsReferralsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [customersById, setCustomersById] = useState({});
  const [customersLoadingId, setCustomersLoadingId] = useState("");
  const [withdrawById, setWithdrawById] = useState({});

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await vsOpsApi.getReferrals();
      setItems(data.items || []);
    } catch (loadError) {
      setError(loadError.message || "Не удалось загрузить рефералку");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      name: item.name,
      refCode: item.refCode,
      email: item.email || "",
      commissionPercent: String(item.commissionPercent),
      paymentWindowDays: String(item.paymentWindowDays),
      customerLifetimeDays:
        item.customerLifetimeDays != null ? String(item.customerLifetimeDays) : "",
      isActive: item.isActive,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage("");
    setError("");
    try {
      const payload = {
        ...form,
        commissionPercent: Number(form.commissionPercent),
        paymentWindowDays: Number(form.paymentWindowDays),
        customerLifetimeDays:
          form.customerLifetimeDays === "" ? null : Number(form.customerLifetimeDays),
      };
      if (editingId) {
        await vsOpsApi.updateReferral(editingId, payload);
        setMessage("Инфлюенсер обновлён");
      } else {
        await vsOpsApi.createReferral(payload);
        setMessage("Инфлюенсер создан");
      }
      resetForm();
      await load();
    } catch (submitError) {
      setError(submitError.message || "Не удалось сохранить");
    }
  };

  const toggleExpanded = async (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    if (customersById[id]) {
      return;
    }
    setCustomersLoadingId(id);
    try {
      const data = await vsOpsApi.getReferralCustomers(id);
      setCustomersById((current) => ({ ...current, [id]: data.customers || [] }));
    } catch (loadError) {
      setError(loadError.message || "Не удалось загрузить рефералов");
    } finally {
      setCustomersLoadingId("");
    }
  };

  const handleWithdraw = async (influencerId) => {
    const draft = withdrawById[influencerId] || {
      paymentMethod: "SBP",
      amount: "",
      note: "",
    };
    setMessage("");
    setError("");
    try {
      const payload =
        draft.paymentMethod === "CRYPTO"
          ? {
              paymentMethod: "CRYPTO",
              amountUsdt: Number(draft.amount),
              note: draft.note,
            }
          : {
              paymentMethod: "SBP",
              amountRub: Number(draft.amount),
              note: draft.note,
            };
      await vsOpsApi.createReferralWithdrawal(influencerId, payload);
      setWithdrawById((current) => ({
        ...current,
        [influencerId]: { paymentMethod: "SBP", amount: "", note: "" },
      }));
      setMessage("Вывод записан");
      await load();
    } catch (withdrawError) {
      setError(withdrawError.message || "Не удалось записать вывод");
    }
  };

  const updateWithdrawDraft = (influencerId, patch) => {
    setWithdrawById((current) => ({
      ...current,
      [influencerId]: {
        paymentMethod: "SBP",
        amount: "",
        note: "",
        ...(current[influencerId] || {}),
        ...patch,
      },
    }));
  };

  return (
    <VsOpsRequireAuth>
      <VsOpsLayout title="Рефералка">
        {error ? <p className="vs-ops-error">{error}</p> : null}
        {message ? <p className="vs-ops-success">{message}</p> : null}

        <form className="vs-ops-settings-form" onSubmit={handleSubmit}>
          <h2 className="vs-ops-subtitle">
            {editingId ? "Редактировать инфлюенсера" : "Новый инфлюенсер"}
          </h2>
          <label className="vs-ops-field">
            Имя
            <input
              value={form.name}
              onChange={(event) =>
                setForm((current) => ({ ...current, name: event.target.value }))
              }
              required
            />
          </label>
          <label className="vs-ops-field">
            Код ref (в ссылке ?ref=)
            <input
              value={form.refCode}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  refCode: event.target.value,
                }))
              }
              required
            />
          </label>
          <label className="vs-ops-field">
            Email инфлюенсера (необязательно, для кэшбэка)
            <input
              type="email"
              value={form.email}
              onChange={(event) =>
                setForm((current) => ({ ...current, email: event.target.value }))
              }
            />
          </label>
          <label className="vs-ops-field">
            % с оборота
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.commissionPercent}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  commissionPercent: event.target.value,
                }))
              }
              required
            />
          </label>
          <label className="vs-ops-field">
            Дней до оплаты после последнего перехода по ref
            <input
              type="number"
              min="1"
              value={form.paymentWindowDays}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  paymentWindowDays: event.target.value,
                }))
              }
              required
            />
          </label>
          <label className="vs-ops-field">
            Срок жизни реферала (пусто = навсегда)
            <input
              type="number"
              min="1"
              placeholder="Навсегда"
              value={form.customerLifetimeDays}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  customerLifetimeDays: event.target.value,
                }))
              }
            />
          </label>
          <label className="vs-ops-field vs-ops-field-toggle">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  isActive: event.target.checked,
                }))
              }
            />
            <span>Активен</span>
          </label>
          <div className="vs-ops-stack">
            <button type="submit" className="vs-ops-btn">
              {editingId ? "Сохранить" : "Создать"}
            </button>
            {editingId ? (
              <button
                type="button"
                className="vs-ops-btn vs-ops-btn-secondary"
                onClick={resetForm}
              >
                Отмена
              </button>
            ) : null}
          </div>
        </form>

        <h2 className="vs-ops-subtitle">Инфлюенсеры</h2>
        {loading ? <p className="vs-ops-muted">Загрузка…</p> : null}

        {!loading && items.length === 0 ? (
          <p className="vs-ops-muted">Пока нет инфлюенсеров</p>
        ) : null}

        <div className="vs-ops-referral-list">
          {items.map((item) => {
            const withdrawDraft = withdrawById[item.id] || {
              paymentMethod: "SBP",
              amount: "",
              note: "",
            };
            const isExpanded = expandedId === item.id;
            const customers = customersById[item.id] || [];

            return (
              <article key={item.id} className="vs-ops-referral-card">
                <div className="vs-ops-referral-card-head">
                  <div>
                    <strong>{item.name}</strong>
                    <p className="vs-ops-muted">
                      ref: <code>{item.refCode}</code>
                      {!item.isActive ? " · выключен" : ""}
                    </p>
                    <p className="vs-ops-muted">
                      Общая ссылка: /?ref={item.refCode}
                    </p>
                    <p className="vs-ops-muted">
                      На магазин: /store/playstation?ref={item.refCode}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="vs-ops-btn vs-ops-btn-secondary"
                    onClick={() => handleEdit(item)}
                  >
                    Изменить
                  </button>
                </div>

                <div className="vs-ops-cards">
                  <div className="vs-ops-card">
                    <span className="vs-ops-card-label">Переходы</span>
                    <strong>{item.clickCount}</strong>
                  </div>
                  <div className="vs-ops-card">
                    <span className="vs-ops-card-label">Клиенты</span>
                    <strong>{item.customersCount}</strong>
                  </div>
                  <div className="vs-ops-card">
                    <span className="vs-ops-card-label">Оборот ₽</span>
                    <strong>{formatRubPrice(item.turnoverRub)}</strong>
                  </div>
                  <div className="vs-ops-card">
                    <span className="vs-ops-card-label">Оборот USDT</span>
                    <strong>{formatUsdtPrice(item.turnoverUsdt)}</strong>
                  </div>
                  <div className="vs-ops-card">
                    <span className="vs-ops-card-label">Заработано ₽</span>
                    <strong>{formatRubPrice(item.earnedRub)}</strong>
                  </div>
                  <div className="vs-ops-card">
                    <span className="vs-ops-card-label">Заработано USDT</span>
                    <strong>{formatUsdtPrice(item.earnedUsdt)}</strong>
                  </div>
                  <div className="vs-ops-card vs-ops-card-accent">
                    <span className="vs-ops-card-label">Баланс ₽</span>
                    <strong>{formatRubPrice(item.balanceRub)}</strong>
                  </div>
                  <div className="vs-ops-card vs-ops-card-accent">
                    <span className="vs-ops-card-label">Баланс USDT</span>
                    <strong>{formatUsdtPrice(item.balanceUsdt)}</strong>
                  </div>
                </div>

                <p className="vs-ops-muted">
                  {item.commissionPercent}% с оборота · окно до оплаты{" "}
                  {item.paymentWindowDays} дн. · клиент{" "}
                  {formatLifetimeDays(item.customerLifetimeDays)}
                </p>

                <div className="vs-ops-referral-withdraw">
                  <label className="vs-ops-field">
                    Вывод
                    <select
                      value={withdrawDraft.paymentMethod}
                      onChange={(event) =>
                        updateWithdrawDraft(item.id, {
                          paymentMethod: event.target.value,
                        })
                      }
                    >
                      <option value="SBP">₽ (СБП)</option>
                      <option value="CRYPTO">USDT</option>
                    </select>
                  </label>
                  <label className="vs-ops-field">
                    Сумма
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={withdrawDraft.amount}
                      onChange={(event) =>
                        updateWithdrawDraft(item.id, {
                          amount: event.target.value,
                        })
                      }
                    />
                  </label>
                  <label className="vs-ops-field">
                    Комментарий
                    <input
                      value={withdrawDraft.note}
                      onChange={(event) =>
                        updateWithdrawDraft(item.id, { note: event.target.value })
                      }
                    />
                  </label>
                  <button
                    type="button"
                    className="vs-ops-btn"
                    onClick={() => handleWithdraw(item.id)}
                  >
                    Записать вывод
                  </button>
                </div>

                <button
                  type="button"
                  className="vs-ops-referral-expand"
                  onClick={() => toggleExpanded(item.id)}
                >
                  {isExpanded ? "Скрыть рефералов" : "Показать рефералов"}
                </button>

                {isExpanded ? (
                  <div className="vs-ops-table-wrap">
                    {customersLoadingId === item.id ? (
                      <p className="vs-ops-muted">Загрузка списка…</p>
                    ) : customers.length === 0 ? (
                      <p className="vs-ops-muted">Пока нет оплативших рефералов</p>
                    ) : (
                      <table className="vs-ops-table vs-ops-table--compact">
                        <thead>
                          <tr>
                            <th>Email</th>
                            <th>Заказов</th>
                            <th>Оборот ₽</th>
                            <th>Оборот USDT</th>
                            <th>Начислено ₽</th>
                            <th>Начислено USDT</th>
                          </tr>
                        </thead>
                        <tbody>
                          {customers.map((row) => (
                            <tr key={row.email}>
                              <td>{row.email}</td>
                              <td>{row.ordersCount}</td>
                              <td>{formatRubPrice(row.turnoverRub)}</td>
                              <td>{formatUsdtPrice(row.turnoverUsdt)}</td>
                              <td>{formatRubPrice(row.earnedRub)}</td>
                              <td>{formatUsdtPrice(row.earnedUsdt)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      </VsOpsLayout>
    </VsOpsRequireAuth>
  );
}

export default VsOpsReferralsPage;
